import { Box, Typography, Grid } from '@mui/material';

import { useCallback, useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { FeatureFlags } from 'context/FeatureFlagWrapper';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PwcLogo } from 'common/assets/images/pwc_logo_landing.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import Footer from 'common/components/layouts/Footer/Footer';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import {
	DEFAULT_TENANT_ID,
	RISK_LINK_LABEL,
	TENANT_VALIDATION_ERROR,
} from 'common/constants/index';
import localStorageWrapper from 'common/loginUtils/local-storage-wrapper';
import storageWrapper from 'common/loginUtils/storage-wrapper';
import { urlWithTenant } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import styles from './LandingPage.module.scss';

const LandingPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { REACT_APP_LOGIN_URL, REACT_APP_SSO_LOGIN_URL } = process.env;

	const featureFlagsContext = useContext(FeatureFlags);
	const { ALLOWED_TENANTS, IS_SSO_LOGIN_ENABLED } = FeatureFlagsKeysEnum;
	const allowedTenants = useFeatureFlag(ALLOWED_TENANTS);
	const isSSOLoginEnabled = useFeatureFlag(IS_SSO_LOGIN_ENABLED);
	const [invalidTenant, setInvalidTenant] = useState<string | null>(null);

	const ssoHandler = useCallback((redirectUrl: string) => {
		const tenantId = window.location.pathname;
		const tenantUrl = tenantId.split('/')[1];
		localStorageWrapper.setStorageItem('tenantId', tenantUrl);
		const allowedTenantsData = allowedTenants
			? JSON.parse(JSON.stringify(allowedTenants))
			: { data: [] }; // Replace with your actual tenant IDs
		const isValidTenant = allowedTenantsData.data.includes(tenantUrl);
		const tenantname = isValidTenant ? tenantUrl : DEFAULT_TENANT_ID;
		const url = `${redirectUrl}?tenant=${encodeURIComponent(tenantname)}`;
		localStorageWrapper.setStorageItem(
			'ssoType',
			redirectUrl === REACT_APP_LOGIN_URL ? 'v1' : 'v2',
		);
		if (isValidTenant) {
			setInvalidTenant(null);
			window.location.href = url as string;
		} else {
			setInvalidTenant(tenantUrl);
		}
	}, []);

	if (!featureFlagsContext) {
		// Handle the case where the context value is undefined
		return null; // or another fallback UI
	}
	useEffect(() => {
		const currentTime = Math.floor(Date.now() / 1000);
		const tokenExpiry = storageWrapper.getStorageItem('expiry');
		const sec25 = 25; // 25 seconds
		const timeDiff = tokenExpiry - currentTime;
		if (tokenExpiry && timeDiff > sec25) {
			// if valid token navigate to dashboard
			navigate(urlWithTenant('/home'));
		}
	}, []);

	return (
		<Grid container flexWrap="nowrap" className={styles['landing-page']}>
			<Grid item xs={7} className={styles['landing-page-left']}>
				<PwcLogo className={styles['logo-icon']} />
				<Box className={styles['login-wrapper']}>
					<Typography className={styles['login-header']}>
						{t('welcome')}
					</Typography>
					<Typography className={styles['login-title']}>
						{RISK_LINK_LABEL}
					</Typography>
					{invalidTenant && (
						<Typography className={styles['err-msg']}>
							{`"${invalidTenant}" ${TENANT_VALIDATION_ERROR}`}
						</Typography>
					)}
					<ButtonComp
						btnType="secondary"
						data-testid="login-button"
						style={{ marginBottom: 25 }}
						clickHandler={() => ssoHandler(String(REACT_APP_LOGIN_URL))}
					>
						{t('Login')}
					</ButtonComp>
					{isSSOLoginEnabled && (
						<ButtonComp
							btnType="primary"
							data-testid="login-button"
							clickHandler={() => ssoHandler(String(REACT_APP_SSO_LOGIN_URL))}
						>
							{t('SSO Login')}
						</ButtonComp>
					)}
				</Box>
				<Box className={styles['login-footer']}>
					<Footer isLandingFooter />
				</Box>
			</Grid>
			<Grid item xs={5} className={styles['landing-page-right']}>
				<Box className={styles['landing-page-right-img']} />
			</Grid>
		</Grid>
	);
};

export default LandingPage;
